import { useCallback, useMemo, type ReactNode } from 'react';
import { useTheme } from '@course-authoring/shared-types';
import { adminTokens } from '../admin-ui';

export interface ChipOption {
  readonly label: string;
  readonly value: string;
  /** Disables this chip only (the row's `disabled` disables all). */
  readonly disabled?: boolean;
  /** Tooltip, e.g. why the chip is disabled. */
  readonly title?: string;
  /** Filled pill for the row's main action, at most one per row. */
  readonly primary?: boolean;
}

export const AI_GENERATE_CHIPS: readonly ChipOption[] = [
  { label: 'Photorealistic', value: 'Photorealistic' },
  { label: 'Diverse team', value: 'Diverse team' },
  { label: 'No text or logos', value: 'No text or logos' },
  { label: 'Flat illustration', value: 'Flat illustration' },
  { label: 'Office setting', value: 'Office setting' },
] as const;

export const AI_EDIT_CHIPS: readonly ChipOption[] = [
  { label: 'Remove background', value: 'Remove background' },
  { label: 'Brighter lighting', value: 'Brighter lighting' },
  { label: 'Add border', value: 'Add border' },
  { label: 'Make more professional', value: 'Make more professional' },
  { label: 'Illustration style', value: 'Illustration style' },
] as const;

interface QuickSelectChipsProps {
  chips: readonly ChipOption[];
  onSelect: (value: string) => void;
  disabled?: boolean;
  /** Accessible name of the chip group. Defaults to "Quick suggestions". */
  groupLabel?: string;
  /** Accessible name per chip. Defaults to `Add "<label>" to prompt`. */
  chipAriaLabel?: (chip: ChipOption) => string;
  /** Flows after the last chip, inside the row (for example a text link). */
  trailing?: ReactNode;
  /** `admin` draws white pills with the admin palette (Author with AI); the default keeps the course tokens. */
  variant?: 'course' | 'admin';
}

export function QuickSelectChips({ chips, onSelect, disabled = false, groupLabel = 'Quick suggestions', chipAriaLabel, trailing, variant = 'course' }: QuickSelectChipsProps) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createStyles(tokens), [tokens]);

  const handleClick = useCallback(
    (value: string) => {
      if (!disabled) {
        onSelect(value);
      }
    },
    [disabled, onSelect]
  );

  return (
    <div style={styles.container} role="group" aria-label={groupLabel}>
      {chips.map((chip) => (
        <button
          key={chip.value}
          type="button"
          style={{
            ...styles.chip,
            ...(variant === 'admin' ? adminChip : {}),
            ...(chip.primary ? (variant === 'admin' ? adminChipPrimary : styles.chipPrimary) : {}),
            ...(disabled || chip.disabled ? styles.chipDisabled : {}),
          }}
          onClick={() => handleClick(chip.value)}
          disabled={disabled || chip.disabled}
          title={chip.title}
          aria-label={chipAriaLabel ? chipAriaLabel(chip) : `Add "${chip.label}" to prompt`}
        >
          {chip.label}
        </button>
      ))}
      {trailing}
    </div>
  );
}

const adminChip: React.CSSProperties = {
  padding: '10px 18px',
  fontFamily: adminTokens.typography.fontFamily,
  fontSize: '14px',
  fontWeight: 500,
  border: `1px solid ${adminTokens.border.control}`,
  borderRadius: adminTokens.radius.pill,
  background: adminTokens.surface.panel,
  color: adminTokens.text.primary,
};

const adminChipPrimary: React.CSSProperties = {
  fontWeight: 600,
  border: `1px solid ${adminTokens.action.primary}`,
  background: adminTokens.action.primary,
  color: adminTokens.text.onPrimary,
};

function createStyles(tokens: ReturnType<typeof useTheme>['tokens']): Record<string, React.CSSProperties> {
  return {
    container: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: '8px',
    },
    chip: {
      padding: '6px 12px',
      fontSize: '13px',
      fontWeight: 500,
      border: `1px solid ${tokens.text.subtle}`,
      borderRadius: '16px',
      background: tokens.backgrounds.surface,
      color: tokens.text.primary,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      transition: `background-color ${tokens.ui.transitionDefault}, border-color ${tokens.ui.transitionDefault}`,
    },
    chipPrimary: {
      fontWeight: 600,
      border: `1px solid ${tokens.brand.primary}`,
      background: tokens.brand.primary,
      color: tokens.text.onBrand,
    },
    chipDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  };
}
